<template>
  <qtm-content-block v-if="po || draftQuote" title="Purchase Order Details">
    <template v-slot:top-right>
      <sync-status v-if="showSync" class="mr-3" :order="order" :po="po!" />
      <slot name="action" />
    </template>
    <v-row class="qtm-body text-dark-grey" no-gutters>
      <v-col class="pr-4" cols="6">
        <details-container :details="poDetails" label-size="8.5em">
          <template v-slot:po-number>
            <copy-text v-if="po?.po_number" :text="po.po_number" />
            <span v-else-if="order.pendingSignature">
              Generated once supplier signs
            </span>
          </template>
          <template v-if="hold" v-slot:date>
            <qtm-chip color="secondary" text-color="white">
              HOLD FOR RELEASE
            </qtm-chip>
          </template>
          <template v-slot:location>
            <div v-if="location" v-text="location.name" />
            <div v-else-if="order.jobsite?.inventory">
              {{ "Inventory" }}
            </div>
            <div v-else class="text-capitalize" v-text="order.delivery_location" />
            <qtm-address v-if="po" :address="po.shipto_address" :city="po.shipto_city" :province="po.shipto_province" />
            <qtm-address v-else :address="order.address" :city="order.city" :province="order.province" />
          </template>
        </details-container>
      </v-col>
      <v-divider class="my-3" vertical />
      <v-col class="pl-4" cols="6">
        <details-container :details="supplierDetails" label-size="6.5em">
          <template v-slot:transfer-from>
            <location-detail :jobsite="order.jobsite" :location="order.transfer_from" />
          </template>
          <template v-slot:supplier>
            <div v-if="supplier">
              <div class="qtm-label" v-text="supplier.name" />
              <qtm-address
                :address="supplier.address"
                :city="supplier.city"
                :postal-code="supplier.postal_code"
                :province="supplier.province"
              />
            </div>
          </template>
          <template v-slot:contact="{ text }">
            <div>
              <div class="d-flex justify-space-between">
                <div v-if="contact" v-text="`${contact.first_name} ${contact.last_name}`" />
                <div v-else v-text="text" />
                <change-contact-dialog
                  v-if="supplier && po && !po.requiresAdjustments && !order.cancelled"
                  activator-class="my-n2"
                  :model-value="contact"
                  preferred
                  :save="updateContact"
                  :vendor="supplier"
                  @update:model-value="onContactChange"
                />
              </div>
              <div v-if="contact">
                <div>
                  <qtm-phone-number :ext="contact.phone_extension" :phone="contact.phone_number" />
                </div>
                <div v-text="contact.email" />
                <supplier-chat-dialog v-if="quote && contact.email" :order="order" :quote="quote" />
              </div>
            </div>
          </template>
        </details-container>
      </v-col>
    </v-row>
  </qtm-content-block>
</template>

<script setup lang="ts">
import type { Contact, Order, PurchaseOrder, Vendor } from '@quotetome/materials-api'
import ChangeContactDialog from '@/components/vendors/change-contact-dialog.vue'
import CopyText from '@/components/copy-text.vue'
import DetailsContainer from '@/components/details-container.vue'
import LocationDetail from '@/components/jobsites/location-detail.vue'
import SupplierChatDialog from '@/components/vendors/supplier-chat-dialog.vue'
import SyncStatus from '@/components/purchase-orders/sync-status.vue'
import { objectDeliveryDate } from '@/models/filters'

export interface Props {
  order: Order
  po?: PurchaseOrder
}

const props = defineProps<Props>()

const hold = computed(() => (props.po || props.order).delivery_time === 'hold')
const pickup = computed(() => !!(props.po ?? props.order).customer_pickup)
const pickupOrDelivery = computed(() => pickup.value ? 'Pickup' : 'Delivery')
const locationLabel = computed(() => {
  if (props.order.isTransfer) {
    return 'Transfer To'
  }

  return `${props.order.jobsite?.inventory ? 'Inventory' : 'Delivery'} Location`
})
const deliveryDateText = computed(() => {
  if ((props.po || props.order).delivery_time === 'tbd') {
    return "Don't know date & time yet"
  }

  return objectDeliveryDate(props.po || props.order)
})
const poDetails = computed(() => [
  { label: 'PO Number', value: 'po-number' },
  {
    label: `${pickupOrDelivery.value} Date`,
    text: deliveryDateText.value,
    value: 'date',
  },
  {
    label: locationLabel.value,
    value: 'location',
    exclude: pickup.value,
  },
].filter(detail => !detail.exclude))

const showSync = computed(() => !!props.po?.latest_change_order?.accounting_status)

const supplierDetails = computed(() => {
  if (props.order.isTransfer) {
    return [ { label: 'Transfer From', value: 'transfer-from' } ]
  }

  return [
    { label: 'Supplier', value: 'supplier' },
    { label: 'Contact', value: 'contact', text: 'Internal PO Distribution Only' },
  ]
})

const draftQuote = computed(() => (props.order.isDraft ? props.order.quotes[0] : undefined))
const quote = computed(() => {
  if (draftQuote.value) {
    return draftQuote.value
  }

  if (props.po?.supplier) {
    return props.order.quotes.find(q => q.vendor?.id === props.po!.supplier)
  }

  return undefined
})
const contact = computed<Contact | null | undefined>(() => {
  if (props.po?.supplier_contact) {
    return {
      id: props.po.supplier_contact,
      email: props.po.supplier_contact_email,
      first_name: props.po.supplier_contact_first_name,
      last_name: props.po.supplier_contact_last_name,
      phone_number: props.po.supplier_contact_phone,
      phone_extension: props.po.supplier_contact_phone_extension,
    } as Contact
  }

  return draftQuote.value?.salesperson
})

const supplier = computed<Vendor | undefined>(() => {
  if (props.po) {
    return {
      id: props.po.supplier as any as number,
      address: props.po.supplier_address,
      city: props.po.supplier_city,
      name: props.po.supplier_name,
      postal_code: props.po.supplier_postal_code,
      province: props.po.supplier_province,
    } as Vendor
  }

  return draftQuote.value?.vendor
})

const location = computed(() => {
  if (props.order.location) {
    return props.order.jobsite?.locations.find(location => location.id === props.order.location)
  }

  return undefined
})

const { $api, $toast } = useNuxtApp()
const updateContact = async (contact: Contact) => {
  await $api.v1.purchaseOrders.updateContact(props.po!.id!, { contact: contact.id })
  $toast.success('New supplier contact has been saved. All future emails about this order will be sent to new contact')
}
const onContactChange = (contact: Contact) => {
  if (props.po) {
    props.po.supplier_contact = contact.id
    props.po.supplier_contact_email = contact.email
    props.po.supplier_contact_first_name = contact.first_name
    props.po.supplier_contact_last_name = contact.last_name
    props.po.supplier_contact_phone = contact.phone_number
    props.po.supplier_contact_phone_extension = contact.phone_extension
  }
}
</script>
